import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users } from 'lucide-react';

const PricingPolicyModal = ({ isOpen, onClose, policyText, extraGuestPrice, roomName }) => {
  const { t, i18n } = useTranslation();
  
  if (!isOpen) return null;

  // Get the policy text for current language
  const currentLang = i18n.language;
  const policy = policyText?.[currentLang] || policyText?.hr || policyText?.en || '';

  // Extra guest price text per language
  const extraGuestText = {
    hr: `Dodatni gost: €${extraGuestPrice} po osobi po noći`,
    en: `Extra guest: €${extraGuestPrice} per person per night`,
    de: `Zusätzlicher Gast: €${extraGuestPrice} pro Person pro Nacht`,
    it: `Ospite extra: €${extraGuestPrice} a persona a notte`
  };

  // Default policy text if none provided
  const defaultPolicy = {
    hr: `• Važeća kreditna kartica je potrebna za plaćanje. Prihvaćene kreditne kartice su: Visa, MasterCard, Diners i American Express.
• Prijava u objekt, moguća je nakon 15:00, na dan dolaska.
• Odjava iz objekta se mora obaviti do 10:00, na dan odlaska.
• Objekt pridržava pravo naplatiti dodatne troškove za zakašnjele odjave.
• Cijene uključuju boravišnu pristojbu i troškove rezerviranja.
• Želite li promijeniti ili otkazati napravljenu rezervaciju, izmjene možete napraviti kontaktirajući e-mailom, u skladu s Uvjetima otkaza rezervacije.
• U slučaju otkaza rezervacije 7 dana ili manje prije dolaska ili nedolaska, povrat novca nije moguć. Rezervacije otkazane prije izvan perioda od 7 dana prije dolaska ili nedolaska bit će refundirane u iznosu od 100%.
• Prije dolaska u objekt na e-mail adresu gosta šalje se link za online Check-in. Popunjavanjem navedenog mogu se unaprijed dostaviti svi potrebni podaci gostiju čime se znatno skraćuje postupak prijave u objekt.
• Za sve dodatne informacije molimo kontaktirajte nas na e-mail: info@rijekabooking.com. Zadržavamo pravo odbiti, otkazati ili izmijeniti rezervaciju ukoliko je došlo do zlouporabe rezervacijskog sustava ili je ista napravljena očiglednom greškom korisnika.
• Rezervacija se može izmijeniti u svakom trenutku u ovisnosti o raspoloživim datumima.`,
    en: `• A valid credit card is required for payment. Accepted credit cards are: Visa, MasterCard, Diners and American Express.
• Check-in is possible after 3:00 PM on the day of arrival.
• Check-out must be completed by 10:00 AM on the day of departure.
• The property reserves the right to charge additional fees for late check-outs.
• Prices include tourist tax and booking fees.
• If you wish to change or cancel your reservation, modifications can be made by contacting us via email, in accordance with the Cancellation Policy.
• In case of cancellation 7 days or less before arrival or no-show, no refund is possible. Reservations cancelled outside of the 7-day period before arrival or no-show will be refunded at 100%.
• Before arriving at the property, a link for online Check-in will be sent to the guest's email address. By filling it out, all necessary guest information can be provided in advance, significantly shortening the check-in process.
• For any additional information, please contact us at: info@rijekabooking.com. We reserve the right to refuse, cancel or modify a reservation if there has been misuse of the booking system or if it was made due to an obvious user error.
• Reservations can be modified at any time depending on available dates.`,
    de: `• Eine gültige Kreditkarte ist für die Zahlung erforderlich. Akzeptierte Kreditkarten sind: Visa, MasterCard, Diners und American Express.
• Der Check-in ist ab 15:00 Uhr am Anreisetag möglich.
• Der Check-out muss bis 10:00 Uhr am Abreisetag erfolgen.
• Die Unterkunft behält sich das Recht vor, zusätzliche Gebühren für späte Check-outs zu erheben.
• Die Preise beinhalten Kurtaxe und Buchungsgebühren.
• Wenn Sie Ihre Reservierung ändern oder stornieren möchten, können Sie Änderungen per E-Mail vornehmen, gemäß den Stornierungsbedingungen.
• Bei Stornierung 7 Tage oder weniger vor Anreise oder bei Nichterscheinen ist keine Rückerstattung möglich. Reservierungen, die außerhalb des 7-Tage-Zeitraums vor Anreise oder Nichterscheinen storniert werden, werden zu 100% erstattet.
• Vor der Ankunft in der Unterkunft wird ein Link für den Online-Check-in an die E-Mail-Adresse des Gastes gesendet. Durch Ausfüllen können alle notwendigen Gastinformationen im Voraus übermittelt werden, was den Check-in-Prozess erheblich verkürzt.
• Für weitere Informationen kontaktieren Sie uns bitte unter: info@rijekabooking.com. Wir behalten uns das Recht vor, eine Reservierung abzulehnen, zu stornieren oder zu ändern, wenn das Buchungssystem missbraucht wurde oder die Buchung aufgrund eines offensichtlichen Benutzerfehlers erfolgte.
• Reservierungen können jederzeit je nach Verfügbarkeit geändert werden.`,
    it: `• Per il pagamento è necessaria una carta di credito valida. Le carte di credito accettate sono: Visa, MasterCard, Diners e American Express.
• Il check-in è possibile dopo le 15:00 del giorno di arrivo.
• Il check-out deve essere completato entro le 10:00 del giorno di partenza.
• La struttura si riserva il diritto di addebitare costi aggiuntivi per i check-out tardivi.
• I prezzi includono la tassa di soggiorno e le spese di prenotazione.
• Se desideri modificare o annullare la tua prenotazione, le modifiche possono essere effettuate contattandoci via email, in conformità con la Politica di Cancellazione.
• In caso di cancellazione 7 giorni o meno prima dell'arrivo o di mancata presentazione, non è possibile alcun rimborso. Le prenotazioni annullate al di fuori del periodo di 7 giorni prima dell'arrivo o della mancata presentazione saranno rimborsate al 100%.
• Prima dell'arrivo nella struttura, verrà inviato un link per il Check-in online all'indirizzo email dell'ospite. Compilandolo, tutte le informazioni necessarie degli ospiti possono essere fornite in anticipo, riducendo significativamente il processo di check-in.
• Per qualsiasi informazione aggiuntiva, contattaci a: info@rijekabooking.com. Ci riserviamo il diritto di rifiutare, annullare o modificare una prenotazione in caso di uso improprio del sistema di prenotazione o se è stata effettuata a causa di un evidente errore dell'utente.
• Le prenotazioni possono essere modificate in qualsiasi momento a seconda della disponibilità.`
  };

  const displayPolicy = policy || defaultPolicy[currentLang] || defaultPolicy.hr;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[var(--color-dark)] text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{t('properties.pricingPolicy')}</h2>
            {roomName && <p className="text-sm text-white/70">{roomName}</p>}
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            data-testid="close-pricing-policy"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-60px)]">
          {/* Extra Guest Price Highlight */}
          {extraGuestPrice > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">
                    {extraGuestText[currentLang] || extraGuestText.hr}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    {currentLang === 'hr' ? 'Cijena za svakog gosta iznad prvog' : 
                     currentLang === 'en' ? 'Price for each guest beyond the first' :
                     currentLang === 'de' ? 'Preis für jeden Gast über den ersten hinaus' :
                     'Prezzo per ogni ospite oltre il primo'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="prose prose-sm max-w-none">
            {displayPolicy.split('\n').map((line, index) => (
              <p key={index} className="mb-3 text-gray-700 leading-relaxed">
                {line}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPolicyModal;
